import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { STATUS_META } from "./meta";
import type { TicketSummary } from "../types";

type T = Record<string, string>;

export function SupportCalendar({
  tickets,
  t,
  language,
}: {
  tickets: TicketSummary[];
  t: T;
  language: string;
}) {
  const navigate = useNavigate();
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  const locale = language === "th" ? th : undefined;

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start, end });
  }, [month]);

  const weekdayHeaders = useMemo(() => {
    const start = startOfWeek(new Date());
    return eachDayOfInterval({ start, end: endOfWeek(new Date()) }).map((d) =>
      format(d, "EEEEEE", { locale }),
    );
  }, [locale]);

  const byDay = useMemo(() => {
    const map = new Map<string, TicketSummary[]>();
    for (const tk of tickets) {
      const key = format(new Date(tk.createdOn), "yyyy-MM-dd");
      const arr = map.get(key) ?? [];
      arr.push(tk);
      map.set(key, arr);
    }
    return map;
  }, [tickets]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900/40">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {format(month, "MMMM yyyy", { locale })}
        </h2>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-full px-3"
            onClick={() => setMonth(startOfMonth(new Date()))}
          >
            {t.calendarToday}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => setMonth((m) => subMonths(m, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => setMonth((m) => addMonths(m, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 px-0.5 pb-1">
        {weekdayHeaders.map((w, i) => (
          <div
            key={i}
            className="text-center text-[11px] font-medium uppercase text-gray-400 dark:text-gray-500"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayTickets = byDay.get(key) ?? [];
          const inMonth = isSameMonth(day, month);
          const today = isToday(day);
          const visible = dayTickets.slice(0, 3);
          const extra = dayTickets.length - visible.length;

          return (
            <div
              key={key}
              className={cn(
                "min-h-[92px] rounded-lg border p-1.5",
                inMonth
                  ? "border-gray-100 bg-gray-50/40 dark:border-gray-800 dark:bg-gray-900/20"
                  : "border-transparent bg-transparent opacity-50",
              )}
            >
              <div
                className={cn(
                  "mb-1 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium",
                  today
                    ? "bg-primary text-white"
                    : "text-gray-500 dark:text-gray-400",
                )}
              >
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {visible.map((tk) => (
                  <button
                    key={tk.name}
                    onClick={() => navigate(`/support/${tk.name}`)}
                    title={tk.subject}
                    className={cn(
                      "flex w-full items-center gap-1 rounded px-1.5 py-0.5 text-left text-[11px] font-medium",
                      STATUS_META[tk.status].pill,
                    )}
                  >
                    <span
                      className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATUS_META[tk.status].dot)}
                    />
                    <span className="truncate">{tk.subject}</span>
                  </button>
                ))}
                {extra > 0 && (
                  <div className="px-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                    {t.calendarMore.replace("{{count}}", String(extra))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
